import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwp254Api, FP610, FP611 } from '../../../services/pwp254Api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './usePWP254Mode';
import { calcDetailRow, calcMasterTotals } from '../helpers';

export function usePWP254Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FP610 | null>(null);
  const [formData, setFormData] = useState<Partial<FP610>>({});
  const [details, setDetails] = useState<Partial<FP611>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{
    data: FP610;
    dets: Partial<FP611>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await pwp254Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setSelectedRow(0);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FP610, value: FP610[keyof FP610]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FP610): FP610[keyof FP610] => {
    return (formData as Record<string, FP610[keyof FP610]>)[key] ?? '';
  }, [formData]);

  /** Recalculate all detail rows and master totals (BR-C01/BR-C02) */
  const recalcAll = useCallback((
    dets: Partial<FP611>[],
    master: Partial<FP610>,
  ): { newDetails: Partial<FP611>[]; newMaster: Partial<FP610> } => {
    const mtsRate = master.fp610_22 || 0;
    const nwRate = master.fp610_23 || 0;
    const gwRate = master.fp610_24 || 0;

    const newDetails = dets.map(d => {
      const row = d as unknown as Record<string, number>;
      const calc = calcDetailRow(row, mtsRate, nwRate, gwRate);
      return { ...d, ...calc };
    });

    const totals = calcMasterTotals(newDetails as unknown as Record<string, number>[]);
    const newMaster = { ...master, ...totals, fp610_16: newDetails.length };
    return { newDetails, newMaster };
  }, []);

  /** Set a detail field value on the selected row, auto-recalculate */
  const setDetailField = useCallback((key: keyof FP611, value: FP611[keyof FP611]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      // If changing a quantity field, recalculate this row
      const qtyFields: (keyof FP611)[] = ['fp611_21', 'fp611_22', 'fp611_23', 'fp611_24', 'fp611_51', 'fp611_52', 'fp611_53', 'fp611_54'];
      if (qtyFields.includes(key)) {
        setFormData(curMaster => {
          const { newDetails, newMaster } = recalcAll(next, curMaster);
          // We set details inside formData callback to avoid stale closures
          setDetails(newDetails);
          return newMaster;
        });
        return next; // return the original; setDetails inside setFormData will override
      }
      return next;
    });
  }, [selectedRow, recalcAll]);

  /** Recalculate when rate fields change (MTS率/NW率/GW率) */
  const setRateField = useCallback((key: keyof FP610, value: number) => {
    setFormData(prev => {
      const updated = { ...prev, [key]: value };
      const { newDetails, newMaster } = recalcAll(details, updated);
      setDetails(newDetails);
      return newMaster;
    });
  }, [details, recalcAll]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FP610) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FP610,
        details: details as FP611[],
      };
      if (mode === 'add') {
        const result = await pwp254Api.create(req);
        message.success(`新增成功: ${result.fp610_01}`);
        onSuccess(result);
      } else {
        const result = await pwp254Api.update(formData.fp610_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map(e => e.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await pwp254Api.delete(record.fp610_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row (BR-F05) */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fp611_02 || 0), 0);
    if (maxSeq >= 999) {
      message.warning('最多 999 個明細項');
      return;
    }
    // Carry over COLOR from previous row
    const prevColor = details.length > 0 ? (details[details.length - 1].fp611_12 || '') : '';
    const newRow: Partial<FP611> = {
      fp611_01: formData.fp610_01 || '',
      fp611_02: maxSeq + 1,
      fp611_12: prevColor,
    };
    setDetails(prev => [...prev, newRow]);
    setFormData(prev => ({ ...prev, fp610_16: (prev.fp610_16 || 0) + 1 }));
    setSelectedRow(details.length); // select the new row
  }, [details, formData.fp610_01]);

  /** Delete the currently selected detail row (BR-F06) */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 0) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      // Renumber (T-F05)
      const renumbered = next.map((d, i) => ({ ...d, fp611_02: i + 1 }));
      setFormData(cur => ({ ...cur, fp610_16: renumbered.length }));
      // Recalculate totals
      const totals = calcMasterTotals(renumbered as unknown as Record<string, number>[]);
      setFormData(cur => ({ ...cur, ...totals }));
      return renumbered;
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
  }, [details.length, selectedRow]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fp610_21: 1,   // default 包裝單位 = R/NO.
      fp610_26: 1,   // default 外銷
      fp610_27: 1,   // default 出貨單位
      fp610_91: user?.userId || '',
    });
    setDetails([]);
    setSelectedRow(0);
  }, [user, clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: FP610) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    setDetailField, setRateField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
  };
}
